import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Header from '../../components/Header';
import SearchableSelect from '../../components/SearchableSelect';
import { colors, radius } from '../../styles/theme';
import { logError } from '../../utils/logError';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : {};
};

// ============ MAIN PAGE ============
const IkincilIslemePage = () => {
  const [malzemeler, setMalzemeler] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [activeEventId, setActiveEventId] = useState(null);
  const [activeEvent, setActiveEvent] = useState(null);
  const [formData, setFormData] = useState({ cikti_malzeme_id: '', cikti_miktar: '', notlar: '' });

  // Girdi ekleme
  const [girdiMalzeme, setGirdiMalzeme] = useState('');
  const [girdiMiktar, setGirdiMiktar] = useState('');
  const [saving, setSaving] = useState(false);

  const showMsg = useCallback((msg, type = 'success') => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(''), 4000);
  }, []);

  const fetchEvents = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/secondary-processing/events?limit=50`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setEvents(Array.isArray(data) ? data : []);
    } catch (err) {
      logError('Events fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEventDetail = useCallback(async (id) => {
    try {
      const res = await fetch(`${API}/api/secondary-processing/events/${id}`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setActiveEvent(data);
    } catch (err) {
      showMsg('Detay yüklenemedi: ' + err.message, 'error');
    }
  }, [showMsg]);

  useEffect(() => {
    fetchEvents();
    fetch(`${API}/api/malzemeler`, { headers: getAuthHeaders() })
      .then(r => r.json()).then(d => setMalzemeler(Array.isArray(d) ? d : [])).catch(() => {});
  }, [fetchEvents]);

  const malzemeOptions = useMemo(() =>
    malzemeler.map(m => ({ value: String(m.id), label: `${m.ad} (${m.birim || 'KG'})` })),
    [malzemeler]
  );

  // Event oluştur
  const handleCreateEvent = async () => {
    if (!formData.cikti_malzeme_id || !formData.cikti_miktar) {
      showMsg('Çıktı malzemesi ve miktar giriniz', 'error');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/secondary-processing/events`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({
          cikti_malzeme_id: parseInt(formData.cikti_malzeme_id),
          cikti_miktar: parseFloat(formData.cikti_miktar),
          notlar: formData.notlar || null
        })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setActiveEventId(data.id);
      await fetchEventDetail(data.id);
      showMsg(`İşlem oluşturuldu: ${data.islem_no}`);
    } catch (err) {
      showMsg('Hata: ' + err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  // Girdi ekle
  const handleAddInput = async () => {
    if (!girdiMalzeme || !girdiMiktar) {
      showMsg('Malzeme ve miktar seçiniz', 'error');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/secondary-processing/events/${activeEventId}/inputs`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({ malzeme_id: parseInt(girdiMalzeme), miktar: parseFloat(girdiMiktar) })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      setGirdiMalzeme('');
      setGirdiMiktar('');
      await fetchEventDetail(activeEventId);
    } catch (err) {
      showMsg('Girdi eklenemedi: ' + err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  // Girdi sil
  const handleRemoveInput = async (inputId) => {
    try {
      const res = await fetch(`${API}/api/secondary-processing/events/${activeEventId}/inputs/${inputId}`, {
        method: 'DELETE', headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await fetchEventDetail(activeEventId);
    } catch (err) {
      showMsg('Silme hatası: ' + err.message, 'error');
    }
  };

  // Tamamla
  const handleComplete = async () => {
    if (!window.confirm('İşlemi tamamlayıp stokları güncellemek istiyor musunuz?')) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/secondary-processing/events/${activeEventId}/complete`, {
        method: 'POST', headers: getAuthHeaders()
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      showMsg(`Tamamlandı! Birim maliyet: ${data.cikti_birim_maliyet?.toFixed(2)} TL/kg, Fire: %${data.fire_orani?.toFixed(1)}`);
      resetForm();
      fetchEvents();
    } catch (err) {
      showMsg('Tamamlama hatası: ' + err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setActiveEventId(null);
    setActiveEvent(null);
    setFormData({ cikti_malzeme_id: '', cikti_miktar: '', notlar: '' });
    setGirdiMalzeme('');
    setGirdiMiktar('');
  };

  // Hesaplama
  const toplamGirdiKg = activeEvent?.toplam_girdi_kg || 0;
  const ciktiMiktar = activeEvent?.cikti_miktar || parseFloat(formData.cikti_miktar) || 0;
  const fireKg = toplamGirdiKg > 0 ? Math.max(0, toplamGirdiKg - ciktiMiktar) : 0;
  const fireOrani = toplamGirdiKg > 0 ? (fireKg / toplamGirdiKg * 100) : 0;
  const toplamMaliyet = activeEvent?.toplam_girdi_maliyet || 0;
  const tahminiMaliyet = ciktiMiktar > 0 ? toplamMaliyet / ciktiMiktar : 0;

  return (
    <div>
      <Header title="İkincil İşleme" />
      <div style={{ padding: '20px', background: colors.background, minHeight: 'calc(100vh - 60px)' }}>

        {message && (
          <div style={{
            background: messageType === 'error' ? '#f8d7da' : '#d4edda',
            padding: '12px 20px', marginBottom: '16px', borderRadius: radius.sm,
            color: messageType === 'error' ? '#721c24' : '#155724',
            borderLeft: `4px solid ${messageType === 'error' ? '#dc3545' : '#28a745'}`
          }}>{message}</div>
        )}

        {/* Aksiyon bar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div style={{ fontSize: '14px', color: colors.textSecondary }}>
            Kıyma çekme, kuşbaşı kesme gibi işlemler
          </div>
          <button
            onClick={() => { if (showForm || activeEventId) resetForm(); else setShowForm(true); }}
            style={{
              padding: '8px 16px', background: (showForm || activeEventId) ? '#6c757d' : '#1976d2',
              color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer',
              fontWeight: 'bold', fontSize: '13px'
            }}
          >
            {(showForm || activeEventId) ? 'Kapat' : '+ Yeni İşlem'}
          </button>
        </div>

        {/* FORM: Yeni işlem oluştur */}
        {showForm && !activeEventId && (
          <div style={{ background: colors.surface, borderRadius: radius.md, padding: '20px', marginBottom: '16px', border: '2px solid #1976d220' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '15px', color: '#1a1a2e' }}>Yeni İşlem</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: '12px', marginBottom: '16px' }}>
              <div>
                <label style={{ fontSize: '12px', color: colors.textSecondary, display: 'block', marginBottom: '4px' }}>Çıktı Malzeme *</label>
                <SearchableSelect
                  options={malzemeOptions}
                  value={formData.cikti_malzeme_id}
                  onChange={(v) => setFormData({ ...formData, cikti_malzeme_id: v })}
                  placeholder="Kıyma, Kuşbaşı..."
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: colors.textSecondary, display: 'block', marginBottom: '4px' }}>Çıktı Miktar (kg) *</label>
                <input
                  type="number" step="0.1" min="0"
                  value={formData.cikti_miktar}
                  onChange={(e) => setFormData({ ...formData, cikti_miktar: e.target.value })}
                  placeholder="9.5"
                  style={{ width: '100%', padding: '10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '14px', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: colors.textSecondary, display: 'block', marginBottom: '4px' }}>Notlar</label>
                <input
                  type="text"
                  value={formData.notlar}
                  onChange={(e) => setFormData({ ...formData, notlar: e.target.value })}
                  placeholder="Opsiyonel..."
                  style={{ width: '100%', padding: '10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '14px', boxSizing: 'border-box' }}
                />
              </div>
            </div>
            <button
              onClick={handleCreateEvent} disabled={saving}
              style={{
                padding: '10px 24px', background: '#1976d2', color: 'white',
                border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontWeight: 'bold', fontSize: '14px',
                opacity: saving ? 0.6 : 1
              }}
            >
              {saving ? 'Oluşturuluyor...' : 'Oluştur & Girdi Ekle'}
            </button>
          </div>
        )}

        {/* FORM: Girdi ekleme (aktif event var) */}
        {activeEventId && activeEvent && (
          <div style={{ background: colors.surface, borderRadius: radius.md, padding: '20px', marginBottom: '16px', border: '2px solid #ff980020' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '16px', color: '#1a1a2e' }}>
                  {activeEvent.islem_no} — {activeEvent.cikti_malzeme_ad}
                </h3>
                <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>
                  Hedef: {activeEvent.cikti_miktar} kg
                </div>
              </div>
              <span style={{
                padding: '4px 12px', borderRadius: radius.xl, fontSize: '12px', fontWeight: 'bold',
                background: '#fff3e0', color: '#e65100'
              }}>TASLAK</span>
            </div>

            {/* Girdi ekleme formu */}
            <div style={{
              display: 'grid', gridTemplateColumns: '2fr 1fr auto', gap: '8px',
              marginBottom: '16px', padding: '12px', background: colors.backgroundAlt, borderRadius: radius.sm
            }}>
              <SearchableSelect
                options={malzemeOptions}
                value={girdiMalzeme}
                onChange={setGirdiMalzeme}
                placeholder="Girdi malzeme seç..."
              />
              <input
                type="number" step="0.1" min="0"
                value={girdiMiktar}
                onChange={(e) => setGirdiMiktar(e.target.value)}
                placeholder="Miktar (kg)"
                style={{ padding: '10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '14px' }}
              />
              <button
                onClick={handleAddInput} disabled={saving}
                style={{
                  padding: '10px 20px', background: '#ff9800', color: 'white',
                  border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontWeight: 'bold', fontSize: '13px',
                  whiteSpace: 'nowrap', opacity: saving ? 0.6 : 1
                }}
              >+ Ekle</button>
            </div>

            {/* Girdi listesi */}
            {activeEvent.girdiler && activeEvent.girdiler.length > 0 && (
              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '16px' }}>
                <thead>
                  <tr style={{ background: colors.background }}>
                    <th style={thStyle}>#</th>
                    <th style={{ ...thStyle, textAlign: 'left' }}>Malzeme</th>
                    <th style={thStyle}>Miktar (kg)</th>
                    <th style={thStyle}>Birim Maliyet</th>
                    <th style={thStyle}>Toplam</th>
                    <th style={thStyle}></th>
                  </tr>
                </thead>
                <tbody>
                  {activeEvent.girdiler.map((g, idx) => (
                    <tr key={g.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                      <td style={tdStyle}>{idx + 1}</td>
                      <td style={{ ...tdStyle, textAlign: 'left', fontWeight: '600' }}>{g.malzeme_ad}</td>
                      <td style={tdStyle}>{g.miktar.toFixed(2)}</td>
                      <td style={tdStyle}>{g.birim_maliyet.toFixed(2)} TL</td>
                      <td style={{ ...tdStyle, fontWeight: '600' }}>{g.toplam_maliyet.toFixed(2)} TL</td>
                      <td style={tdStyle}>
                        <button
                          onClick={() => handleRemoveInput(g.id)}
                          style={{
                            padding: '3px 8px', background: '#f8d7da', color: '#721c24',
                            border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontSize: '11px'
                          }}
                        >Sil</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Özet */}
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '16px'
            }}>
              <SummaryCard label="Toplam Girdi" value={`${toplamGirdiKg.toFixed(2)} kg`} color="#1976d2" />
              <SummaryCard label="Çıktı" value={`${ciktiMiktar.toFixed(2)} kg`} color="#2e7d32" />
              <SummaryCard label="Fire" value={`${fireKg.toFixed(2)} kg (%${fireOrani.toFixed(1)})`}
                color={fireOrani > 15 ? '#dc3545' : '#ff9800'} />
              <SummaryCard label="Tahmini Maliyet" value={`${tahminiMaliyet.toFixed(2)} TL/kg`} color="#7b1fa2" />
            </div>

            {/* Tamamla butonu */}
            {activeEvent.girdiler && activeEvent.girdiler.length > 0 && (
              <button
                onClick={handleComplete} disabled={saving || ciktiMiktar > toplamGirdiKg}
                style={{
                  padding: '12px 32px', background: ciktiMiktar > toplamGirdiKg ? '#ccc' : '#2e7d32',
                  color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer',
                  fontWeight: 'bold', fontSize: '14px', opacity: saving ? 0.6 : 1
                }}
              >
                {saving ? 'Tamamlanıyor...' : 'Tamamla & Stokları Güncelle'}
              </button>
            )}
            {ciktiMiktar > toplamGirdiKg && toplamGirdiKg > 0 && (
              <div style={{ fontSize: '12px', color: '#dc3545', marginTop: '8px' }}>
                Çıktı miktarı ({ciktiMiktar} kg) girdi toplamından ({toplamGirdiKg.toFixed(2)} kg) büyük olamaz
              </div>
            )}
          </div>
        )}

        {/* LİSTE: Tamamlanan işlemler */}
        {!showForm && !activeEventId && (
          loading ? (
            <div style={{ background: colors.surface, borderRadius: radius.md, padding: '40px', textAlign: 'center' }}>
              <div style={{ fontSize: '14px', color: '#888' }}>Yükleniyor...</div>
            </div>
          ) : events.length === 0 ? (
            <div style={{ background: colors.surface, borderRadius: radius.md, padding: '40px', textAlign: 'center' }}>
              <div style={{ fontSize: '40px', marginBottom: '12px' }}>&#x1F504;</div>
              <div style={{ fontSize: '14px', color: '#888' }}>Henüz işlem yapılmamış</div>
              <div style={{ fontSize: '12px', color: '#aaa', marginTop: '4px' }}>
                "Yeni İşlem" ile kıyma çekme, kuşbaşı kesme gibi işlemler oluşturabilirsiniz
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {events.map(ev => (
                <div key={ev.id} style={{
                  background: colors.surface, borderRadius: radius.md, padding: '16px',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                }}>
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#1a1a2e' }}>
                      {ev.cikti_malzeme_ad} — {ev.cikti_miktar} kg
                    </div>
                    <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>
                      {ev.islem_no} | {ev.girdi_sayisi} girdi | Fire: %{(ev.fire_orani || 0).toFixed(1)} | {ev.islem_tarihi}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {ev.cikti_birim_maliyet > 0 && (
                      <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#7b1fa2' }}>
                        {ev.cikti_birim_maliyet.toFixed(2)} TL/kg
                      </span>
                    )}
                    <span style={{
                      padding: '4px 10px', borderRadius: radius.xl, fontSize: '11px', fontWeight: 'bold',
                      background: ev.durum === 'TAMAMLANDI' ? '#e8f5e9' : '#fff3e0',
                      color: ev.durum === 'TAMAMLANDI' ? '#2e7d32' : '#e65100'
                    }}>{ev.durum}</span>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
};

// ============ HELPER COMPONENTS ============
const SummaryCard = ({ label, value, color }) => (
  <div style={{
    background: `${color}08`, border: `1px solid ${color}30`, borderRadius: radius.md,
    padding: '8px', textAlign: 'center'
  }}>
    <div style={{ fontSize: '11px', color: colors.textSecondary, marginBottom: '4px' }}>{label}</div>
    <div style={{ fontSize: '16px', fontWeight: 'bold', color }}>{value}</div>
  </div>
);

const thStyle = { padding: '8px 12px', fontSize: '12px', color: colors.textSecondary, fontWeight: '600', textAlign: 'center' };
const tdStyle = { padding: '10px 12px', fontSize: '13px', textAlign: 'center' };

export default IkincilIslemePage;
